import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { ImageCropper } from '../components/ImageCropper';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  User,
  Building,
  MapPin,
  Phone,
  Globe,
  Save,
  CreditCard,
  DollarSign,
  Clock,
  CheckCircle,
  Upload,
  Image,
  Trash2,
  Briefcase,
  Landmark,
  ExternalLink,
  Banknote,
  History,
  ImagePlus,
  FolderOpen,
  Plus,
  Bell,
  Mail,
  Loader2,
  Trophy,
  Award
} from 'lucide-react';
import { useFoundingStatus } from '../hooks/useFoundingStatus';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const POSITION_TITLES = [
  'Owner',
  'CEO',
  'President',
  'Vice President',
  'Managing Director',
  'Director',
  'Senior Partner',
  'Partner',
  'Managing Broker',
  'Broker',
  'Senior Agent',
  'Agent',
  'Associate',
  'Consultant',
  'Advisor',
  'Manager',
  'Team Lead',
  'Specialist',
  'Coordinator',
  'Representative',
  'Other'
];


const NOTIFICATION_LABELS = {
  connection_requests: { label: 'Connection Requests', desc: 'When someone sends you a connection request' },
  connection_accepted: { label: 'Connection Accepted', desc: 'When someone accepts your connection request' },
  direct_messages: { label: 'Direct Messages', desc: 'When you receive a private message' },
  group_invites: { label: 'Group Invitations', desc: 'When you are invited to join a group' },
  event_reminders: { label: 'Event Reminders', desc: 'Upcoming event notifications and reminders' },
  profile_views: { label: 'Profile Views', desc: 'When someone views your profile' },
  weekly_digest: { label: 'Weekly Digest', desc: 'A weekly summary of network activity' },
  marketing_emails: { label: 'News & Updates', desc: 'Product updates, tips, and announcements' },
  ad_slot_updates: { label: 'Ad Slot Updates', desc: 'Bids, approvals, and ad marketplace activity' },
  referral_notifications: { label: 'Referral Notifications', desc: 'When your referrals sign up or earn rewards' },
};

const NotificationsTab = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getNotificationPreferences().then(data => setPrefs(data.preferences)).catch(() => {
      setPrefs(Object.fromEntries(Object.keys(NOTIFICATION_LABELS).map(k => [k, true])));
    });
  }, []);

  const toggle = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.updateNotificationPreferences(prefs);
      toast.success('Notification preferences saved!');
    } catch {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return <div className="py-8 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6">
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" /> Email Notifications
          </CardTitle>
          <CardDescription>Choose which emails you want to receive from ConnectMailAgent</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1">
          {Object.entries(NOTIFICATION_LABELS).map(([key, { label, desc }]) => (
            <div
              key={key}
              className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
              onClick={() => toggle(key)}
              data-testid={`notif-toggle-${key}`}
            >
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
              <div className={`w-11 h-6 rounded-full relative transition-colors ${prefs[key] ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${prefs[key] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Button onClick={savePrefs} disabled={saving} className="gap-2" data-testid="save-notifications-btn">
        <Save className="w-4 h-4" />
        {saving ? 'Saving...' : 'Save Preferences'}
      </Button>
    </div>
  );
};


const StripeConnectSection = () => {
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [savingPaypal, setSavingPaypal] = useState(false);
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [activeMethod, setActiveMethod] = useState('paypal');

  const fetchAll = async () => {
    try {
      const [connectData, payoutData] = await Promise.all([
        api.getConnectStatus().catch(() => ({ status: 'not_connected' })),
        api.getPayoutInfo().catch(() => ({ payout_method: 'platform_credit', paypal_email: '' })),
      ]);
      setConnectStatus(connectData);
      setPayoutInfo(payoutData);
      setPaypalEmail(payoutData.paypal_email || '');
      setActiveMethod(payoutData.payout_method || 'paypal');
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchAll();
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') === 'return') {
      const poll = setInterval(async () => {
        const data = await api.getConnectStatus();
        setConnectStatus(data);
        if (data.status === 'active') {
          clearInterval(poll);
          toast.success('Bank account connected successfully!');
        }
      }, 2000);
      setTimeout(() => clearInterval(poll), 20000);
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const data = await api.setupStripeConnect(window.location.origin);
      if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to create Stripe account';
      toast.error(msg);
    } finally { setConnecting(false); }
  };

  const handleSavePaypal = async () => {
    if (!paypalEmail.trim() || !paypalEmail.includes('@')) {
      toast.error('Please enter a valid PayPal email');
      return;
    }
    setSavingPaypal(true);
    try {
      await api.savePayPalEmail(paypalEmail.trim());
      setActiveMethod('paypal');
      toast.success('PayPal email saved! Payouts will be sent here.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save PayPal email');
    } finally { setSavingPaypal(false); }
  };

  const handleViewDashboard = async () => {
    try {
      const data = await api.getConnectDashboard();
      if (data.url) window.open(data.url, '_blank');
    } catch { toast.error('Failed to open Stripe dashboard'); }
  };

  const handleSetMethod = async (method) => {
    try {
      await api.updatePayoutMethod(method);
      setActiveMethod(method);
      toast.success(`Payout method updated to ${method === 'paypal' ? 'PayPal' : 'Bank Account'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update payout method');
    }
  };

  if (loading) return <div className="animate-pulse h-20 bg-muted rounded-lg" />;

  return (
    <div className="space-y-5" data-testid="payout-section">
      {/* PayPal Option */}
      <div className={`p-4 rounded-lg border-2 transition-colors ${activeMethod === 'paypal' ? 'border-blue-400 bg-blue-50/50' : 'border-border'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z" fill="#253B80"/><path d="M18.171 7.394c-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9L4.86 23.197a.407.407 0 0 0 .402.47h3.5c.46 0 .85-.334.922-.788l.038-.196.73-4.631.047-.254a.929.929 0 0 1 .922-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.173-3.388-.777-4.471a3.717 3.717 0 0 0-1.068-.82 11.4 11.4 0 0 1-.35 1.62z" fill="#179BD7"/></svg>
            </div>
            <div>
              <p className="text-sm font-semibold">PayPal</p>
              <p className="text-[11px] text-muted-foreground">Receive payouts to your PayPal account</p>
            </div>
          </div>
          {activeMethod === 'paypal' && payoutInfo?.paypal_email && (
            <Badge variant="outline" className="text-blue-600 border-blue-300 text-[10px]">Active</Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter your PayPal email address"
            value={paypalEmail}
            onChange={e => setPaypalEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSavePaypal()}
            className="flex-1 h-9 text-sm"
            data-testid="paypal-email-input"
          />
          <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700" onClick={handleSavePaypal} disabled={savingPaypal} data-testid="save-paypal-btn">
            {savingPaypal ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
        {payoutInfo?.paypal_email && activeMethod !== 'paypal' && (
          <Button variant="link" size="sm" className="text-xs text-blue-600 mt-1 h-auto p-0" onClick={() => handleSetMethod('paypal')}>
            Set as active payout method
          </Button>
        )}
      </div>

      {/* Bank Account (Stripe Connect) Option */}
      <div className={`p-4 rounded-lg border-2 transition-colors ${activeMethod === 'stripe_connect' ? 'border-green-400 bg-green-50/50' : 'border-border'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
              <Landmark className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-semibold">Bank Account</p>
              <p className="text-[11px] text-muted-foreground">Direct deposit to your bank via Stripe</p>
            </div>
          </div>
          {connectStatus?.status === 'active' && (
            <Badge variant="outline" className="text-green-600 border-green-300 text-[10px]">
              {activeMethod === 'stripe_connect' ? 'Active' : 'Connected'}
            </Badge>
          )}
        </div>

        {connectStatus?.status === 'active' ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>Bank account connected</span>
            </div>
            <div className="flex items-center gap-2">
              {activeMethod !== 'stripe_connect' && (
                <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => handleSetMethod('stripe_connect')}>
                  Set as active
                </Button>
              )}
              <Button variant="ghost" size="sm" className="text-xs h-8" onClick={handleViewDashboard} data-testid="stripe-dashboard-btn">
                <ExternalLink className="w-3 h-3 mr-1" />Stripe Dashboard
              </Button>
            </div>
          </div>
        ) : connectStatus?.status === 'pending' || connectStatus?.status === 'pending_verification' ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {connectStatus.status === 'pending_verification' ? 'Stripe is verifying your information (1-2 days).' : 'Setup started but not completed.'}
            </p>
            <Button size="sm" className="h-8 text-xs" onClick={handleConnect} disabled={connecting}>
              <Banknote className="w-3.5 h-3.5 mr-1" />
              {connecting ? 'Loading...' : 'Continue Setup'}
            </Button>
          </div>
        ) : (
          <div>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleConnect} disabled={connecting}>
              <Banknote className="w-3.5 h-3.5 mr-1" />
              {connecting ? 'Loading...' : 'Add Bank Account'}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-2">Select "Individual" during setup — no business required.</p>
          </div>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground text-center">
        Your ad revenue (50% of each ad booking) is deposited to your active payout method.
      </p>
    </div>
  );
};

export const Settings = () => {
  const { user, refreshProfile } = useAuth();
  const founding = useFoundingStatus();
  const [professions, setProfessions] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    profession_id: '',
    custom_profession: '',
    position_title: '',
    custom_position: '',
    company: '',
    phone: '',
    website: '',
    logo_url: '',
    profile_photo_url: '',
    additional_images: [],
    linkedin_url: '',
    twitter_url: '',
    facebook_url: '',
    instagram_url: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    ad_button_color: ''
  });
  const [transactions, setTransactions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ logo: false, photo: false, additional: false });
  const [cropState, setCropState] = useState({ open: false, src: null, type: null, aspect: 1, shape: 'rect' });
  const [imageHistory, setImageHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [galleryDialog, setGalleryDialog] = useState({ open: false, type: null }); // type: 'logo', 'photo', 'additional'

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [professionsData, transactionsData] = await Promise.all([
          api.getProfessions(),
          api.getPaymentHistory()
        ]);
        setProfessions(professionsData.professions || []);
        setTransactions(transactionsData.transactions || []);
        
        if (user) {
          setFormData({
            full_name: user.full_name || '',
            profession_id: user.profession_id || '',
            custom_profession: user.custom_profession || '',
            position_title: user.position_title || '',
            custom_position: user.custom_position || '',
            company: user.company || '',
            phone: user.phone || '',
            website: user.website || '',
            logo_url: user.logo_url || '',
            profile_photo_url: user.profile_photo_url || '',
            additional_images: user.additional_images || [],
            linkedin_url: user.linkedin_url || '',
            twitter_url: user.twitter_url || '',
            facebook_url: user.facebook_url || '',
            instagram_url: user.instagram_url || '',
            street_address: user.street_address || '',
            city: user.city || '',
            state: user.state || '',
            zip_code: user.zip_code || '',
            ad_button_color: user.ad_button_color || ''
          });
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Open cropper for logo, photo, and additional images
    if (type === 'additional') {
      const reader = new FileReader();
      reader.onload = () => {
        setCropState({
          open: true,
          src: reader.result,
          type: 'additional',
          aspect: 1,
          shape: 'rect',
          allowAspectChange: true
        });
      };
      reader.readAsDataURL(file);
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        setCropState({
          open: true,
          src: reader.result,
          type,
          aspect: type === 'photo' ? 1 : 1,
          shape: type === 'photo' ? 'round' : 'rect',
          allowAspectChange: false
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropDone = async (croppedBlob) => {
    const file = new File([croppedBlob], `cropped_${cropState.type}.jpg`, { type: 'image/jpeg' });
    await uploadAndSave(file, cropState.type);
    setCropState({ open: false, src: null, type: null, aspect: 1, shape: 'rect', allowAspectChange: false });
  };

  const uploadAndSave = async (file, type) => {
    setUploading(prev => ({ ...prev, [type]: true }));
    try {
      const result = await api.uploadImage(file);
      const relativeUrl = result.url;

      let profileUpdate = {};
      if (type === 'logo') {
        updateField('logo_url', relativeUrl);
        profileUpdate = { logo_url: relativeUrl };
      } else if (type === 'photo') {
        updateField('profile_photo_url', relativeUrl);
        profileUpdate = { profile_photo_url: relativeUrl };
      } else if (type === 'additional') {
        if (formData.additional_images.length < 5) {
          const newImages = [...formData.additional_images, relativeUrl];
          updateField('additional_images', newImages);
          profileUpdate = { additional_images: newImages };
        } else {
          toast.error('Maximum 5 additional images allowed');
          return;
        }
      }

      await api.updateProfile(profileUpdate);
      await refreshProfile();
      toast.success('Image uploaded and saved!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const removeAdditionalImage = async (index) => {
    const newImages = formData.additional_images.filter((_, i) => i !== index);
    updateField('additional_images', newImages);
    // Auto-save removal to backend
    try {
      await api.updateProfile({ additional_images: newImages });
      await refreshProfile();
    } catch (error) {
      console.error('Failed to save removal:', error);
    }
  };

  const fetchImageHistory = async () => {
    if (imageHistory.length > 0) return; // Already loaded
    setHistoryLoading(true);
    try {
      const data = await api.getImageHistory();
      setImageHistory(data.images || []);
    } catch (error) {
      console.error('Failed to fetch image history:', error);
      toast.error('Failed to load image history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openGallery = async (type) => {
    setGalleryDialog({ open: true, type });
    await fetchImageHistory();
  };

  const selectFromGallery = async (imageUrl) => {
    const type = galleryDialog.type;
    try {
      if (type === 'logo') {
        updateField('logo_url', imageUrl);
        await api.updateProfile({ logo_url: imageUrl });
      } else if (type === 'photo') {
        updateField('profile_photo_url', imageUrl);
        await api.updateProfile({ profile_photo_url: imageUrl });
      } else if (type === 'additional') {
        if (formData.additional_images.length < 5) {
          const newImages = [...formData.additional_images, imageUrl];
          updateField('additional_images', newImages);
          await api.updateProfile({ additional_images: newImages });
        } else {
          toast.error('Maximum 5 additional images allowed');
          return;
        }
      }
      await refreshProfile();
      toast.success('Image applied!');
      setGalleryDialog({ open: false, type: null });
    } catch (error) {
      console.error('Failed to apply image:', error);
      toast.error('Failed to apply image');
    }
  };

  const applyImageFromHistory = async (imageUrl, type) => {
    try {
      if (type === 'logo') {
        updateField('logo_url', imageUrl);
        await api.updateProfile({ logo_url: imageUrl });
      } else if (type === 'photo') {
        updateField('profile_photo_url', imageUrl);
        await api.updateProfile({ profile_photo_url: imageUrl });
      } else if (type === 'additional') {
        if (formData.additional_images.length < 5) {
          const newImages = [...formData.additional_images, imageUrl];
          updateField('additional_images', newImages);
          await api.updateProfile({ additional_images: newImages });
        } else {
          toast.error('Maximum 5 additional images allowed');
          return;
        }
      }
      await refreshProfile();
      toast.success('Image applied successfully!');
    } catch (error) {
      console.error('Failed to apply image:', error);
      toast.error('Failed to apply image');
    }
  };

  const deleteFromHistory = async (imageId) => {
    try {
      await api.deleteImage(imageId);
      setImageHistory(prev => prev.filter(img => img.id !== imageId));
      toast.success('Image deleted from history');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to delete image');
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateProfile(formData);
      await refreshProfile();
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-96 bg-muted rounded-lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight">My Profile</h1>
          <p className="text-muted-foreground mt-1">
            Manage your info, images, notifications and billing
          </p>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-4 max-w-xl">
            <TabsTrigger value="profile" data-testid="profile-tab">
              <User className="w-4 h-4 mr-2" />
              My Info
            </TabsTrigger>
            <TabsTrigger value="images" data-testid="images-tab">
              <Image className="w-4 h-4 mr-2" />
              Images
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="notifications-tab">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="billing" data-testid="billing-tab">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
          </TabsList>

          {/* PROFILE TAB */}
          <TabsContent value="profile" className="mt-6">
            <div className="space-y-6">
              {/* Personal Information */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Personal Information</CardTitle>
                  <CardDescription>Your basic contact details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="full_name"
                          value={formData.full_name}
                          onChange={(e) => updateField('full_name', e.target.value)}
                          className="pl-10"
                          data-testid="fullname-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => updateField('phone', e.target.value)}
                          className="pl-10"
                          placeholder="(555) 123-4567"
                          data-testid="phone-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="website"
                        value={formData.website}
                        onChange={(e) => updateField('website', e.target.value)}
                        className="pl-10"
                        placeholder="https://yourwebsite.com"
                        data-testid="website-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Professional Details */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Professional Details</CardTitle>
                  <CardDescription>Your profession, company, and position</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Profession</Label>
                      <Select
                        value={formData.profession_id}
                        onValueChange={(value) => updateField('profession_id', value)}
                      >
                        <SelectTrigger data-testid="profession-select">
                          <SelectValue placeholder="Select profession" />
                        </SelectTrigger>
                        <SelectContent>
                          {professions.map((prof) => (
                            <SelectItem key={prof.id} value={prof.id}>
                              {prof.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.profession_id === 'custom' && (
                      <div className="space-y-2">
                        <Label htmlFor="custom_profession">Custom Profession</Label>
                        <Input
                          id="custom_profession"
                          value={formData.custom_profession}
                          onChange={(e) => updateField('custom_profession', e.target.value)}
                          placeholder="Enter your profession"
                          data-testid="custom-profession-input"
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">Company Name</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => updateField('company', e.target.value)}
                          className="pl-10"
                          placeholder="Your Company LLC"
                          data-testid="company-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Position / Title</Label>
                      <Select
                        value={formData.position_title}
                        onValueChange={(value) => updateField('position_title', value)}
                      >
                        <SelectTrigger data-testid="position-select">
                          <SelectValue placeholder="Select your position" />
                        </SelectTrigger>
                        <SelectContent>
                          {POSITION_TITLES.map((title) => (
                            <SelectItem key={title} value={title}>
                              {title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {formData.position_title === 'Other' && (
                    <div className="space-y-2">
                      <Label htmlFor="custom_position">Custom Position Title</Label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="custom_position"
                          value={formData.custom_position}
                          onChange={(e) => updateField('custom_position', e.target.value)}
                          className="pl-10"
                          placeholder="Your title (e.g., Founding Partner)"
                          data-testid="custom-position-input"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Address */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Business Address</CardTitle>
                  <CardDescription>Your full business address for the signature</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="street_address">Street Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="street_address"
                        value={formData.street_address}
                        onChange={(e) => updateField('street_address', e.target.value)}
                        className="pl-10"
                        placeholder="123 Main Street, Suite 100"
                        data-testid="street-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => updateField('city', e.target.value)}
                        placeholder="Los Angeles"
                        data-testid="city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Select
                        value={formData.state}
                        onValueChange={(value) => updateField('state', value)}
                      >
                        <SelectTrigger data-testid="state-select">
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">ZIP Code</Label>
                      <Input
                        id="zip_code"
                        value={formData.zip_code}
                        onChange={(e) => updateField('zip_code', e.target.value)}
                        placeholder="90210"
                        data-testid="zip-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Social Media */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Social Media Links</CardTitle>
                  <CardDescription>Add your social profiles to display on signature</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="linkedin_url">LinkedIn</Label>
                      <Input
                        id="linkedin_url"
                        value={formData.linkedin_url}
                        onChange={(e) => updateField('linkedin_url', e.target.value)}
                        placeholder="https://linkedin.com/in/yourprofile"
                        data-testid="linkedin-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="twitter_url">Twitter / X</Label>
                      <Input
                        id="twitter_url"
                        value={formData.twitter_url}
                        onChange={(e) => updateField('twitter_url', e.target.value)}
                        placeholder="https://twitter.com/yourhandle"
                        data-testid="twitter-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="facebook_url">Facebook</Label>
                      <Input
                        id="facebook_url"
                        value={formData.facebook_url}
                        onChange={(e) => updateField('facebook_url', e.target.value)}
                        placeholder="https://facebook.com/yourpage"
                        data-testid="facebook-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="instagram_url">Instagram</Label>
                      <Input
                        id="instagram_url"
                        value={formData.instagram_url}
                        onChange={(e) => updateField('instagram_url', e.target.value)}
                        placeholder="https://instagram.com/yourhandle"
                        data-testid="instagram-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ad Appearance */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Ad Appearance</CardTitle>
                  <CardDescription>Customize how your ads appear in other professionals' signatures</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <Label>Ad Call Button Color</Label>
                    <p className="text-xs text-muted-foreground">This color will be used for the "Call Now" button when your ad appears in signatures</p>
                    <div className="flex flex-wrap gap-3">
                      {[
                        { id: 'green', name: 'Green', value: '#22c55e' },
                        { id: 'blue', name: 'Blue', value: '#3b82f6' },
                        { id: 'purple', name: 'Purple', value: '#8b5cf6' },
                        { id: 'orange', name: 'Orange', value: '#f97316' },
                        { id: 'red', name: 'Red', value: '#ef4444' },
                        { id: 'teal', name: 'Teal', value: '#14b8a6' },
                        { id: 'pink', name: 'Pink', value: '#ec4899' },
                        { id: 'amber', name: 'Amber', value: '#f59e0b' }
                      ].map((color) => (
                        <button
                          key={color.id}
                          onClick={() => updateField('ad_button_color', color.value)}
                          className={`relative w-12 h-12 rounded-lg border-2 transition-all flex items-center justify-center ${
                            formData.ad_button_color === color.value
                              ? 'border-foreground scale-110 shadow-lg'
                              : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                          data-testid={`ad-color-${color.id}`}
                        >
                          {formData.ad_button_color === color.value && (
                            <CheckCircle className="w-5 h-5 text-white drop-shadow-lg" />
                          )}
                        </button>
                      ))}
                    </div>
                    {formData.ad_button_color && (
                      <div className="flex items-center gap-3 mt-3 p-3 bg-muted/50 rounded-lg">
                        <span className="text-sm text-muted-foreground">Preview:</span>
                        <button
                          className="px-4 py-2 text-sm font-semibold rounded-lg text-white shadow-md"
                          style={{ backgroundColor: formData.ad_button_color }}
                        >
                          Call Now
                        </button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving} data-testid="save-profile-btn" size="lg">
                  {saving ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save All Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* IMAGES TAB */}
          <TabsContent value="images" className="mt-6">
            <div className="space-y-6">
              {/* Company Picture */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Company Logo</CardTitle>
                  <CardDescription>Your company logo (appears in signature)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-6">
                    <div className="w-32 h-32 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/50">
                      {formData.logo_url ? (
                        <img src={resolveImageUrl(formData.logo_url)} alt="Company" className="w-full h-full object-contain" />
                      ) : (
                        <Building className="w-10 h-10 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          onClick={() => openGallery('logo')}
                          data-testid="logo-gallery-btn"
                        >
                          <FolderOpen className="w-4 h-4 mr-2" />
                          Image Gallery
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'logo')}
                          className="hidden"
                          id="logo-upload"
                        />
                        <label htmlFor="logo-upload">
                          <Button variant="outline" className="cursor-pointer" asChild disabled={uploading.logo}>
                            <span>
                              {uploading.logo ? 'Uploading...' : (
                                <>
                                  <Plus className="w-4 h-4 mr-2" />
                                  Upload New
                                </>
                              )}
                            </span>
                          </Button>
                        </label>
                      </div>
                      {formData.logo_url && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-600"
                          onClick={async () => {
                            updateField('logo_url', '');
                            await api.updateProfile({ logo_url: '' });
                            await refreshProfile();
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                      <p className="text-xs text-muted-foreground">PNG, JPG, or GIF. Max 2MB. Square format recommended.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Profile Photo */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Profile Photo</CardTitle>
                  <CardDescription>Your professional headshot (appears in signature)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-6">
                    <div className="w-32 h-32 rounded-full border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/50">
                      {formData.profile_photo_url ? (
                        <img src={resolveImageUrl(formData.profile_photo_url)} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-10 h-10 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          onClick={() => openGallery('photo')}
                          data-testid="photo-gallery-btn"
                        >
                          <FolderOpen className="w-4 h-4 mr-2" />
                          Image Gallery
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'photo')}
                          className="hidden"
                          id="photo-upload"
                        />
                        <label htmlFor="photo-upload">
                          <Button variant="outline" className="cursor-pointer" asChild disabled={uploading.photo}>
                            <span>
                              {uploading.photo ? 'Uploading...' : (
                                <>
                                  <Plus className="w-4 h-4 mr-2" />
                                  Upload New
                                </>
                              )}
                            </span>
                          </Button>
                        </label>
                      </div>
                      {formData.profile_photo_url && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-600"
                          onClick={async () => {
                            updateField('profile_photo_url', '');
                            await api.updateProfile({ profile_photo_url: '' });
                            await refreshProfile();
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                      <p className="text-xs text-muted-foreground">PNG or JPG. Max 2MB. Use a professional headshot.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Images */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Additional Images</CardTitle>
                  <CardDescription>Extra images for your profile (certifications, awards, etc.) - Max 5</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                      {formData.additional_images.map((img, index) => (
                        <div key={index} className="relative group">
                          <div className="w-full aspect-square rounded-lg border border-border overflow-hidden bg-muted/50">
                            <img src={resolveImageUrl(img)} alt={`Additional ${index + 1}`} className="w-full h-full object-cover" />
                          </div>
                          <button
                            onClick={() => removeAdditionalImage(index)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {formData.additional_images.length < 5 && (
                        <>
                          {/* Gallery button for additional images */}
                          <div 
                            className="w-full aspect-square rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => openGallery('additional')}
                            data-testid="additional-gallery-btn"
                          >
                            <div className="text-center p-4">
                              <FolderOpen className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                              <span className="text-xs text-muted-foreground">From Gallery</span>
                            </div>
                          </div>
                          {/* Upload new button */}
                          <div className="w-full aspect-square rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e, 'additional')}
                              className="hidden"
                              id="additional-upload"
                            />
                            <label htmlFor="additional-upload" className="cursor-pointer text-center p-4">
                              <Plus className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                              <span className="text-xs text-muted-foreground">Upload New</span>
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formData.additional_images.length}/5 images uploaded
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Image Gallery Dialog */}
              <Dialog open={galleryDialog.open} onOpenChange={(open) => setGalleryDialog({ open, type: galleryDialog.type })}>
                <DialogContent className="sm:max-w-2xl" data-testid="image-gallery-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <FolderOpen className="w-5 h-5 text-accent" />
                      Image Gallery
                    </DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    {historyLoading ? (
                      <div className="text-center py-8 text-muted-foreground">Loading images...</div>
                    ) : imageHistory.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No images in gallery yet</p>
                        <p className="text-xs mt-2">Upload new images to build your gallery</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Select an image to use as your {galleryDialog.type === 'logo' ? 'company logo' : galleryDialog.type === 'photo' ? 'profile photo' : 'additional image'}
                        </p>
                        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3 max-h-[400px] overflow-y-auto">
                          {imageHistory.map((img) => (
                            <div 
                              key={img.id} 
                              className="relative group cursor-pointer"
                              onClick={() => selectFromGallery(img.url)}
                            >
                              <div className="w-full aspect-square rounded-lg border-2 border-transparent hover:border-accent overflow-hidden bg-muted/50 transition-all">
                                <img 
                                  src={resolveImageUrl(img.url)} 
                                  alt="Gallery" 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="absolute inset-0 bg-accent/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                <CheckCircle className="w-8 h-8 text-accent" />
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFromHistory(img.id);
                                }}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Delete from gallery"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving} data-testid="save-images-btn" size="lg">
                  {saving ? 'Saving...' : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Images
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* NOTIFICATIONS TAB */}
          <TabsContent value="notifications" className="mt-6">
            <NotificationsTab />
          </TabsContent>

          {/* BILLING TAB */}
          <TabsContent value="billing" className="mt-6">
            <div className="space-y-6">
              {/* Founding Member Badge — for first-1,000 users (auto-granted) */}
              {founding?.is_founding_member && (
                <Card className="border-amber-300/60 bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-50 shadow-sm" data-testid="settings-founding-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg ring-2 ring-amber-200">
                          <Trophy className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-extrabold text-amber-900 font-['Outfit']" data-testid="settings-founding-title">
                              🏆 Founding Member
                            </p>
                            {founding.founding_member_number && (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]">
                                #{founding.founding_member_number} of 1,000
                              </Badge>
                            )}
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 text-[10px]">
                              Premium for Life
                            </Badge>
                          </div>
                          <p className="text-xs text-amber-900/80 mt-1 max-w-md">
                            One of the first 1,000 members. Premium access included for life — no subscription required.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Account Status */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Account Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${user?.is_premium ? 'bg-green-100' : 'bg-yellow-100'}`}>
                        {user?.is_premium ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-yellow-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium" data-testid="account-status-title">
                          {founding?.is_founding_member ? '🏆 Founding Member' : user?.is_premium ? 'Premium Member' : 'Free Account'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {founding?.is_founding_member
                            ? 'Free Premium access for life — granted automatically as one of the first 1,000 members.'
                            : user?.is_premium ? 'Full access to all features' : 'Limited features'}
                        </p>
                      </div>
                    </div>
                    <Badge variant={user?.is_premium ? 'default' : 'secondary'}>
                      {user?.is_premium ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-4 border border-border rounded-lg">
                      <p className="text-sm text-muted-foreground">Ad Slots</p>
                      <p className="text-2xl font-bold">{(user?.ad_slots_owned || 2) + (user?.additional_ad_slots || 0)}</p>
                    </div>
                    <div className="p-4 border border-border rounded-lg">
                      <p className="text-sm text-muted-foreground">Total Earnings</p>
                      <p className="text-2xl font-bold text-green-600">${user?.total_earnings?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stripe Connect - Bank Payouts */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Landmark className="w-5 h-5" />
                    Payout Settings
                  </CardTitle>
                  <CardDescription>Add your bank account where you want your ad revenue deposited</CardDescription>
                </CardHeader>
                <CardContent>
                  <StripeConnectSection />
                </CardContent>
              </Card>

              {/* Payment History */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Payment History</CardTitle>
                  <CardDescription>Your recent transactions</CardDescription>
                </CardHeader>
                <CardContent>
                  {transactions.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 bg-muted rounded-lg mx-auto mb-4 flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground text-sm">No transactions yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.map((tx) => (
                        <div key={tx.id} className="flex items-center justify-between p-3 border border-border rounded-lg" data-testid={`transaction-${tx.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                              <DollarSign className="w-5 h-5 text-accent" />
                            </div>
                            <div>
                              <p className="font-medium capitalize">{tx.package_type?.replace('_', ' ')}</p>
                              <p className="text-sm text-muted-foreground">
                                {new Date(tx.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">${tx.amount?.toFixed(2)}</p>
                            <Badge variant={tx.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                              {tx.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ImageCropper
        open={cropState.open}
        onClose={() => setCropState({ open: false, src: null, type: null, aspect: 1, shape: 'rect', allowAspectChange: false })}
        imageSrc={cropState.src}
        onCropComplete={handleCropDone}
        aspectRatio={cropState.aspect}
        shape={cropState.shape}
        allowAspectChange={cropState.allowAspectChange || false}
      />
    </Layout>
  );
};

export default Settings;
